import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  PUBLIC_API_SCOPES,
  PUBLIC_API_WEBHOOK_EVENTS,
  PublicApiKey,
  PublicApiScope,
  PublicApiWebhook,
  PublicApiWebhookEvent,
} from '@/shared/api/public-api';
import { Button } from '@/shared/ui/lib/Button';
import { useGetConfirmation } from '@/shared/ui/Confirmations';
import { Loader } from '@/shared/ui/Loader';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { CustomTypography } from '@/shared/ui/Typography';
import { useNotifications } from '@/shared/ui/Notifications';

import {
  useCreatePublicApiKey,
  useDeletePublicApiWebhook,
  usePublicApiChannels,
  usePublicApiKeys,
  usePublicApiWebhooks,
  useRevokePublicApiKey,
  useRotatePublicApiKey,
  useUpsertPublicApiWebhook,
} from '../model/usePublicApiAdmin';
import { PublicApiDocumentation } from './PublicApiDocumentation';
import {
  PublicApiKeyRow,
  PublicApiWebhookRow,
  TokenAlert,
  channelLabel,
  checkboxGroupSx,
  compactEmptyStateSx,
  controlSx,
  eventLabel,
  scopeLabel,
} from './PublicApiSettings.helpers';

const API_BASE_URL = 'https://api.letsgochat.ru/api/public/v1';

export const PublicApiSettings = () => {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  const { closeConfirmation, getConfirmation, setPending } =
    useGetConfirmation();
  const [keyName, setKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<PublicApiScope[]>(['*']);
  const [visibleToken, setVisibleToken] = useState<string | null>(null);
  const [visibleSecret, setVisibleSecret] = useState<string | null>(null);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState<PublicApiWebhookEvent[]>([
    'message.created',
  ]);

  const keysQuery = usePublicApiKeys();
  const channelsQuery = usePublicApiChannels();
  const webhooksQuery = usePublicApiWebhooks(selectedChannelId);
  const createKey = useCreatePublicApiKey();
  const revokeKey = useRevokePublicApiKey();
  const rotateKey = useRotatePublicApiKey();
  const upsertWebhook = useUpsertPublicApiWebhook();
  const deleteWebhook = useDeletePublicApiWebhook();

  useEffect(() => {
    if (!selectedChannelId && channelsQuery.data?.[0]) {
      setSelectedChannelId(channelsQuery.data[0].id);
    }
  }, [channelsQuery.data, selectedChannelId]);

  const selectedChannel = useMemo(
    () =>
      channelsQuery.data?.find((channel) => channel.id === selectedChannelId),
    [channelsQuery.data, selectedChannelId]
  );

  const keys = keysQuery.data ?? [];
  const webhooks = webhooksQuery.data ?? [];

  const copyText = async (value: string) => {
    await navigator.clipboard.writeText(value);
    addNotification({
      type: 'success',
      title: t('integrations:publicApi.copied'),
    });
  };

  const toggleScope = (scope: PublicApiScope, checked: boolean) => {
    if (scope === '*') {
      setSelectedScopes(checked ? ['*'] : []);
      return;
    }

    setSelectedScopes((prev) => {
      const withoutAll = prev.filter((value) => value !== '*');
      if (checked) return [...withoutAll, scope];
      return withoutAll.filter((value) => value !== scope);
    });
  };

  const toggleWebhookEvent = (
    event: PublicApiWebhookEvent,
    checked: boolean
  ) => {
    setWebhookEvents((prev) => {
      if (checked) return [...prev, event];
      return prev.filter((value) => value !== event);
    });
  };

  const submitKey = async () => {
    const key = await createKey.mutateAsync({
      name: keyName.trim(),
      scopes: selectedScopes,
    });
    setKeyName('');
    setSelectedScopes(['*']);
    if (key.token) setVisibleToken(key.token);
  };

  const submitWebhook = async () => {
    if (!selectedChannelId) return;
    const webhook = await upsertWebhook.mutateAsync({
      channelId: selectedChannelId,
      url: webhookUrl.trim(),
      events: webhookEvents,
    });
    setWebhookUrl('');
    setWebhookEvents(['message.created']);
    if (webhook.signing_secret) setVisibleSecret(webhook.signing_secret);
  };

  const isLoading = keysQuery.isPending || channelsQuery.isPending;

  const confirmRevokeKey = async (apiKey: PublicApiKey) => {
    const isConfirmed = await getConfirmation({
      title: t('integrations:publicApi.keys.revokeDialog.title'),
      description: t('integrations:publicApi.keys.revokeDialog.description', {
        name: apiKey.name,
      }),
      confirmText: t('integrations:publicApi.keys.revokeDialog.confirm'),
      priorityButton: 'reject',
      shouldCloseOnConfirm: false,
    });

    if (!isConfirmed) return;

    setPending(true);
    try {
      await revokeKey.mutateAsync(apiKey.id);
      closeConfirmation();
    } finally {
      setPending(false);
    }
  };

  const confirmDeleteWebhook = async (webhook: PublicApiWebhook) => {
    const isConfirmed = await getConfirmation({
      title: t('integrations:publicApi.webhooks.deleteDialog.title'),
      description: t(
        'integrations:publicApi.webhooks.deleteDialog.description',
        {
          url: webhook.url,
        }
      ),
      confirmText: t('integrations:publicApi.webhooks.deleteDialog.confirm'),
      priorityButton: 'reject',
      shouldCloseOnConfirm: false,
    });

    if (!isConfirmed) return;

    setPending(true);
    try {
      await deleteWebhook.mutateAsync({
        channelId: webhook.channel_id,
        webhookId: webhook.id,
      });
      closeConfirmation();
    } finally {
      setPending(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2 }}>
        <Stack spacing={1.75}>
          <Box>
            <CustomTypography
              variant="h3"
              sx={{
                fontSize: { xs: 32, md: 42 },
                fontWeight: 500,
                lineHeight: 1.08,
              }}
            >
              {t('integrations:publicApi.title')}
            </CustomTypography>
            <CustomTypography
              color="text.secondary"
              sx={{ mt: 0.5, fontSize: 16 }}
            >
              {API_BASE_URL}
            </CustomTypography>
          </Box>

          {visibleToken ? (
            <TokenAlert
              label={t('integrations:publicApi.keys.createdToken')}
              value={visibleToken}
              onClose={() => setVisibleToken(null)}
            />
          ) : null}

          {visibleSecret ? (
            <TokenAlert
              label={t('integrations:publicApi.webhooks.signingSecret')}
              value={visibleSecret}
              onClose={() => setVisibleSecret(null)}
            />
          ) : null}

          <Divider />

          <Stack spacing={1.5}>
            <CustomTypography
              variant="h4"
              sx={{ fontSize: 20, fontWeight: 600 }}
            >
              {t('integrations:publicApi.keys.title')}
            </CustomTypography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                fullWidth
                size="small"
                label={t('integrations:publicApi.keys.name')}
                value={keyName}
                onChange={(event) => setKeyName(event.target.value)}
                sx={controlSx}
              />
              <Button
                variant="contained"
                disabled={
                  !keyName.trim() ||
                  selectedScopes.length === 0 ||
                  createKey.isPending
                }
                loading={createKey.isPending}
                onClick={submitKey}
                sx={{ borderRadius: '12px', minHeight: 40, minWidth: 170 }}
              >
                {t('integrations:publicApi.keys.create')}
              </Button>
            </Stack>
            <Stack direction="row" flexWrap="wrap" sx={checkboxGroupSx}>
              {PUBLIC_API_SCOPES.map((scope) => (
                <FormControlLabel
                  key={scope}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedScopes.includes(scope)}
                      onChange={(event) =>
                        toggleScope(scope, event.target.checked)
                      }
                    />
                  }
                  label={scopeLabel(scope, t)}
                />
              ))}
            </Stack>
            {isLoading ? (
              <Loader containerSize="medium" />
            ) : keys.length ? (
              <Stack spacing={1}>
                {keys.map((apiKey) => (
                  <PublicApiKeyRow
                    key={apiKey.id}
                    apiKey={apiKey}
                    t={t}
                    disabledRotate={rotateKey.isPending}
                    disabledRevoke={revokeKey.isPending}
                    onCopyPrefix={() => copyText(apiKey.prefix)}
                    onRotate={async () => {
                      const rotated = await rotateKey.mutateAsync(apiKey.id);
                      if (rotated.token) setVisibleToken(rotated.token);
                    }}
                    onRevoke={() => confirmRevokeKey(apiKey)}
                  />
                ))}
              </Stack>
            ) : (
              <NotFoundData
                title={t('integrations:publicApi.keys.empty')}
                caption={t('integrations:publicApi.keys.emptyCaption')}
                sx={compactEmptyStateSx}
              />
            )}
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            <CustomTypography
              variant="h4"
              sx={{ fontSize: 20, fontWeight: 600 }}
            >
              {t('integrations:publicApi.webhooks.title')}
            </CustomTypography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <FormControl
                fullWidth
                disabled={!channelsQuery.data?.length}
                size="small"
                sx={controlSx}
              >
                <InputLabel id="public-api-channel-label">
                  {t('integrations:publicApi.webhooks.channel')}
                </InputLabel>
                <Select
                  labelId="public-api-channel-label"
                  label={t('integrations:publicApi.webhooks.channel')}
                  value={selectedChannelId}
                  onChange={(event) => setSelectedChannelId(event.target.value)}
                >
                  {channelsQuery.data?.map((channel) => (
                    <MenuItem key={channel.id} value={channel.id}>
                      {channelLabel(channel)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                size="small"
                label={t('integrations:publicApi.webhooks.url')}
                value={webhookUrl}
                onChange={(event) => setWebhookUrl(event.target.value)}
                sx={controlSx}
              />
              <Button
                variant="contained"
                disabled={
                  !selectedChannelId ||
                  !webhookUrl.trim() ||
                  webhookEvents.length === 0 ||
                  upsertWebhook.isPending
                }
                loading={upsertWebhook.isPending}
                onClick={submitWebhook}
                sx={{ borderRadius: '12px', minHeight: 40, minWidth: 170 }}
              >
                {t('common:buttons.save')}
              </Button>
            </Stack>

            <Stack direction="row" flexWrap="wrap" sx={checkboxGroupSx}>
              {PUBLIC_API_WEBHOOK_EVENTS.map((event) => (
                <FormControlLabel
                  key={event}
                  control={
                    <Checkbox
                      size="small"
                      checked={webhookEvents.includes(event)}
                      onChange={(changeEvent) =>
                        toggleWebhookEvent(event, changeEvent.target.checked)
                      }
                    />
                  }
                  label={eventLabel(event, t)}
                />
              ))}
            </Stack>

            {selectedChannel ? (
              webhooksQuery.isPending ? (
                <Loader containerSize="medium" />
              ) : webhooks.length ? (
                <Stack spacing={1}>
                  {webhooks.map((webhook) => (
                    <PublicApiWebhookRow
                      key={webhook.id}
                      webhook={webhook}
                      t={t}
                      disabledDelete={deleteWebhook.isPending}
                      onCopyUrl={() => copyText(webhook.url)}
                      onDelete={() => confirmDeleteWebhook(webhook)}
                    />
                  ))}
                </Stack>
              ) : (
                <NotFoundData
                  title={t('integrations:publicApi.webhooks.empty')}
                  caption={t('integrations:publicApi.webhooks.emptyCaption')}
                  sx={compactEmptyStateSx}
                />
              )
            ) : (
              <NotFoundData
                title={t('integrations:publicApi.webhooks.noChannels')}
                caption={t('integrations:publicApi.webhooks.noChannelsCaption')}
                sx={compactEmptyStateSx}
              />
            )}
          </Stack>

          <Divider />

          <PublicApiDocumentation />
        </Stack>
      </Paper>
    </Stack>
  );
};
